/**
 * Menu roles — the one rule that turns a membership's role flags into the menu
 * audiences (`navigation.ts` `audience` values) a person sees.
 *
 * Ported from mobile's `getNavigationRole` / `getAllUserNavigationRoles`
 * (`apps/mobile/src/utils/user-roles.ts`) with one addition: a member who holds
 * no role flag at all (alumni, past parents, past teachers, trustees) resolves
 * to `community` instead of falling back to the faculty menus. Everyone else
 * resolves exactly as before, staff-to-faculty included.
 */

/** The raw per-school role booleans of a membership. */
export interface MenuRoleFlags {
  isStudent?: boolean
  isParent?: boolean
  isFaculty?: boolean
  isTeacher?: boolean
  isStaff?: boolean
  isAdmin?: boolean
  isCandidate?: boolean
}

/** The menu audience a member resolves to. */
export type MenuRole = 'faculty' | 'parent' | 'student' | 'community'

/** Any of these set makes the member a non-community member. */
const IDENTITY_FLAGS = [
  'isStudent',
  'isParent',
  'isFaculty',
  'isTeacher',
  'isStaff',
  'isAdmin',
  'isCandidate'
] as const satisfies readonly (keyof MenuRoleFlags)[]

/** Whether the member holds no role flag at all. An admin-only or candidate-only member is not community. */
export function isCommunityMember(flags: MenuRoleFlags): boolean {
  return !IDENTITY_FLAGS.some(flag => flags[flag] === true)
}

/**
 * The single menu role used for menu filtering.
 * Priority: community (no flags) > student > faculty|teacher|admin > parent > faculty (staff, candidate).
 */
export function getPrimaryMenuRole(flags: MenuRoleFlags): MenuRole {
  if (isCommunityMember(flags)) return 'community'
  // Student wins even if the user is admin/faculty (they're testing)
  if (flags.isStudent) return 'student'
  if (flags.isFaculty || flags.isTeacher || flags.isAdmin) return 'faculty'
  // Parent is checked before staff so staff+parent users get parent menus
  if (flags.isParent) return 'parent'
  // Staff-only and candidate-only users get faculty menus
  return 'faculty'
}

/** Every menu role the member holds, for multi-role menu merging. */
export function getAllMenuRoles(flags: MenuRoleFlags): MenuRole[] {
  if (isCommunityMember(flags)) return ['community']
  const roles: MenuRole[] = []
  // Staff maps to faculty for menu visibility
  if (flags.isAdmin || flags.isFaculty || flags.isTeacher || flags.isStaff) roles.push('faculty')
  if (flags.isParent) roles.push('parent')
  if (flags.isStudent) roles.push('student')
  // Candidate-only users fall back to faculty
  return roles.length > 0 ? roles : ['faculty']
}
